import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";
import { useT } from "../i18n";
import LanguageToggle from "../components/LanguageToggle";
import ScoreGauge from "../components/ScoreGauge";
import KTBanner from "../components/KTBanner";
import { Button } from "../components/ui/button";
import { Lock, Sparkles, Copy, Check, ShieldCheck } from "lucide-react";

export default function Results({ lang, setLang, languages }) {
  const { resultId } = useParams();
  const tr = useT(lang);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const [checkingPayment, setCheckingPayment] = useState(false);
  const [priceEur, setPriceEur] = useState(9.88);

  useEffect(() => {
    axios.get(`${API}/config/pricing`).then((r) => {
      if (typeof r?.data?.price_eur === "number") setPriceEur(r.data.price_eur);
    }).catch(() => {});
  }, []);
  const fmtPrice = (n) => (lang === "it" ? n.toFixed(2).replace(".", ",") : n.toFixed(2));

  const load = () =>
    axios.get(`${API}/assessments/${resultId}?lang=${lang}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));

  const checkPayment = async (silent = false) => {
    if (!silent) setCheckingPayment(true);
    try {
      const r = await axios.get(`${API}/payments/check_result/${resultId}`);
      if (r.data.paid) {
        if (!silent) toast.success(lang === "it" ? "Pagamento confermato" : "Payment confirmed");
        // Refresh data and navigate to the paid plan.
        try { await load(); } catch {}
        setTimeout(() => window.location.assign(`/plan/${resultId}`), 300);
        return true;
      }
      if (!silent) toast.info(lang === "it"
        ? "Nessun pagamento verificato ancora. Riprova tra qualche secondo."
        : "No payment verified yet. Please try again in a few seconds.");
    } catch {
      if (!silent) toast.error(lang === "it" ? "Impossibile verificare il pagamento" : "Could not verify payment");
    } finally {
      if (!silent) setCheckingPayment(false);
    }
    return false;
  };

  useEffect(() => {
    load();
    // Auto-check for a completed payment on mount — covers the common case
    // where the customer paid on Shopify but was not redirected back to our
    // /payment/success page. Silent poll: 3 attempts spaced 2s apart.
    let attempts = 0;
    const tick = async () => {
      if (attempts >= 3) return;
      attempts += 1;
      const paid = await checkPayment(true);
      if (!paid) setTimeout(tick, 2000);
    };
    setTimeout(tick, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultId]);

  // Re-fetch when the user toggles EN/IT so the personalized (dynamic) text
  // updates immediately in the selected language.
  useEffect(() => {
    if (!resultId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const unlock = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        result_id: resultId,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || "",
      });
      // Free-unlock via 100% promo → straight to the plan
      if (res.data.free_unlock) {
        toast.success(lang === "it" ? "Promo applicata — accesso sbloccato!" : "Promo applied — unlocked!");
        await load();
        return;
      }
      const url = res.data.checkout_url;
      if (!url) throw new Error("Missing checkout_url");
      setCheckoutUrl(url);
      window.location.assign(url);
    } catch (e) {
      const serverMsg = e?.response?.data?.detail;
      toast.error(
        serverMsg
          || (lang === "it" ? "Impossibile aprire il pagamento" : "Could not open payment"),
        { duration: 8000 }
      );
      setCheckoutLoading(false);
    }
  };

  // --- Promo code preview (checkout-time, admin-configurable discount %) ---
  const [promoInput, setPromoInput] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const previewPromo = async () => {
    const c = promoInput.trim().toUpperCase();
    if (!c) return;
    setPromoBusy(true);
    try {
      const r = await axios.post(`${API}/promo/preview`, { code: c });
      setAppliedPromo(r.data);
      toast.success(
        r.data.will_fully_unlock
          ? (lang === "it" ? "Codice valido — accesso gratuito!" : "Valid code — free unlock!")
          : (lang === "it" ? `Promo applicata — nuovo prezzo €${r.data.adjusted_price_eur.toFixed(2).replace('.', ',')}` : `Promo applied — new price €${r.data.adjusted_price_eur.toFixed(2)}`)
      );
    } catch (e) {
      setAppliedPromo(null);
      const s = e?.response?.status;
      toast.error(
        s === 410
          ? (lang === "it" ? "Codice scaduto o esaurito" : "Code expired or used up")
          : (lang === "it" ? "Codice non valido" : "Invalid code")
      );
    } finally {
      setPromoBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success(tr.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6F6B66] text-sm kt-tracking uppercase">…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#6F6B66]">Not found</div>;

  const isPaid = !!data.plan_purchased;
  const isMale = data.flow === "male";

  // Paid users landing on /results/:id are sent straight to their plan.
  if (isPaid && typeof window !== "undefined" && !window.location.pathname.startsWith("/plan")) {
    setTimeout(() => window.location.assign(`/plan/${resultId}`), 200);
  }
  const free = data.free_preview || {};
  const isUnder18 = !!free.is_under_18;
  const isPregnant = !!free.is_pregnant;
  const isBreastfeeding = !!free.is_breastfeeding;
  const lifeStageNote = free.life_stage_note;
  const gentleActive = !!free.gentle_mode && (isUnder18 || isPregnant || isBreastfeeding);

  return (
    <div className="min-h-screen bg-white" data-testid="results-page">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b kt-line">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="block leading-tight" data-testid="hairtu-header">
            <Link to="/" className="font-serif text-xl text-[#383633] tracking-tight hover:text-[#4A4238]">
              HAIRTU
            </Link>
            <div className="text-[9px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.brand_powered}</div>
            <div className="text-[9px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.brand_tagline}</div>
            <a
              href="https://www.ktmilano.com"
              target="_blank"
              rel="noreferrer"
              data-testid="header-ktmilano-link"
              className="text-[10px] text-[#6F6B66] hover:text-[#383633] underline decoration-[#D8C4A5] underline-offset-2"
            >
              {tr.brand_site}
            </a>
          </div>
          <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-4 sm:py-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-4"
        >
          <ScoreGauge value={free.overall_score || 0} label={isMale ? (lang === "it" ? "Punteggio Cosmetico" : "Cosmetic Score") : tr.overall} compact />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">
              {isMale
                ? (lang === "it" ? "— Il tuo Check Cuoio & Capelli" : "— Your Scalp & Hair Check")
                : `— ${tr.results_kicker}`}
            </div>
            <h1 className="font-serif text-lg sm:text-xl leading-snug text-[#383633] tracking-tight break-words" data-testid="results-heading">
              <span>{data.first_name}, </span>
              <span className="text-[#6F6B66]">{tr.results_thanks}</span>
            </h1>
          </div>
        </motion.div>

        {/* Locked one-sentence teaser (female only) */}
        {!isMale && (
          <p className="mt-3 text-[#383633] leading-snug max-w-md font-serif text-sm sm:text-base" data-testid="one-sentence-profile">
              <span data-testid="profile-teaser">{free.profile_teaser || free.one_sentence_profile || ""}</span>
              {!isPaid && (
                <span
                  data-testid="profile-lock"
                  className="inline-flex items-center gap-1 ml-2 text-[10px] tracking-[0.24em] uppercase text-[#B08A5B] align-middle"
                >
                  <Lock className="h-3 w-3" />
                  {lang === "it" ? "Bloccato" : "Locked"}
                </span>
              )}
            </p>
          )}

        {/* Main priority — surfaced first, right below the gauge/teaser (routine-first UX) */}
        {!isMale && free.main_priority_title && (
          <div className="mt-5 sm:mt-6 bg-[#F8F6F2] border kt-line rounded-2xl p-4 sm:p-5 kt-shadow" data-testid="main-priority">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">{tr.main_priority}</div>
            <div className="font-serif text-xl sm:text-2xl text-[#383633] leading-tight tracking-tight" data-testid="main-priority-title">
              {free.main_priority_title}
            </div>
            {(free.three_priorities_v2 || [])[0]?.reason && (
              <p className="mt-1.5 text-sm text-[#6F6B66] leading-snug">
                {(free.three_priorities_v2 || [])[0].reason}
              </p>
            )}
          </div>
        )}

        {/* Compact locked row — 2 remaining priorities as tight teasers */}
        {!isMale && !isPaid && (
          <div className="mt-3 grid grid-cols-2 gap-3" data-testid="three-priorities">
            {[1, 2].map((i) => (
              <div
                key={`locked-${i}`}
                data-testid={`priority-locked-${i - 1}`}
                className="relative overflow-hidden rounded-2xl border kt-line bg-white p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">0{i + 1}</span>
                  <Lock className="h-3.5 w-3.5 text-[#B08A5B]" />
                </div>
                <div className="font-serif text-sm text-[#383633] tracking-tight blur-[3px] select-none">
                  {lang === "it" ? "Priorità personalizzata" : "Personalized priority"}
                </div>
                <div className="text-[11px] text-[#6F6B66] mt-1 blur-[3px] select-none">
                  {lang === "it" ? "Sblocca il piano completo." : "Unlock the full plan."}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Promo code block is rendered INSIDE the "Personalized Plan" card below,
            just above the unlock CTA — see the `promo-block` element there. */}

        {/* Large warm-accent unlock CTA — removed to leave one unified purchase CTA
            in the "Personalized Plan" card further below. Promo block remains,
            and the bottom CTA reflects any applied promo/discounted price. */}

        {isMale && (
          <div className="mt-12 space-y-4" data-testid="male-free-block">
            <div className="bg-[#F8F6F2] border kt-line rounded-2xl p-6 kt-shadow text-center">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                {lang === "it" ? "Il tuo profilo cosmetico" : "Your cosmetic profile"}
              </div>
              <div
                className="font-serif text-2xl sm:text-3xl text-[#383633] tracking-tight inline-flex items-center gap-2 justify-center"
                data-testid="male-profile-name"
              >
                <span>{free.profile_teaser || free.profile_name || ""}</span>
                {!isPaid && (
                  <span
                    data-testid="male-profile-lock"
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase text-[#B08A5B] align-middle"
                  >
                    <Lock className="h-3 w-3" /> {lang === "it" ? "Bloccato" : "Locked"}
                  </span>
                )}
              </div>
            </div>

            {/* Observation 01 fully visible; the rest are locked cards */}
            <div className="bg-white border kt-line rounded-2xl p-6" data-testid="male-observations-wrap">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
                {lang === "it" ? "Osservazione principale" : "Main observation"}
              </div>
              <ul className="space-y-3" data-testid="male-observations">
                {(free.observations || []).slice(0, 1).map((o, i) => (
                  <li key={i} className="text-sm text-[#383633] leading-relaxed flex gap-3">
                    <span className="text-[#A8AD94] font-mono text-xs mt-0.5">01</span>
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Locked observations 02+ */}
            {!isPaid && (free.locked_observations_count || 0) > 0 && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="male-locked-observations">
                {Array.from({ length: Math.min(2, free.locked_observations_count) }).map((_, i) => (
                  <div
                    key={i}
                    data-testid={`male-observation-locked-${i}`}
                    className="relative overflow-hidden rounded-2xl border kt-line bg-[#F8F6F2] p-5 kt-shadow"
                  >
                    <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">0{i + 2}</div>
                    <div className="font-serif text-base text-[#383633] tracking-tight mb-2 blur-[3px] select-none">
                      {lang === "it" ? "Osservazione personalizzata" : "Personalized observation"}
                    </div>
                    <div className="text-sm text-[#6F6B66] leading-relaxed blur-[3px] select-none">
                      {lang === "it"
                        ? "Il consiglio completo è disponibile con il piano di 4 settimane."
                        : "The full observation is available with your 4-week plan."}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-white/45 backdrop-blur-[2px]">
                      <div className="flex flex-col items-center gap-1.5 text-[#6F5439]">
                        <Lock className="h-5 w-5" />
                        <div className="text-[10px] kt-tracking-wider uppercase">
                          {lang === "it" ? `Sblocca con €${fmtPrice(priceEur)}` : `Unlock with €${fmtPrice(priceEur)}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Immediate tip — hidden entirely in free view */}
            {isPaid && free.immediate_tip && (
              <div className="bg-white border kt-line rounded-2xl p-6">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                  {lang === "it" ? "Suggerimento immediato" : "One immediate tip"}
                </div>
                <p className="font-serif text-lg text-[#383633] leading-snug" data-testid="male-tip">
                  {free.immediate_tip}
                </p>
              </div>
            )}

            {/* Male purchase CTA — removed; the unified "Personalized Plan"
                unlock card below is now the ONE purchase point for both
                male and female flows. Existing checkoutPayment / promo /
                shopify logic remains unchanged. */}
          </div>
        )}

        {/* Gentle Routine Mode banner (life-stage safety) */}
        {gentleActive && lifeStageNote && (
          <div className="mt-6 border kt-line rounded-2xl p-5 bg-white flex items-start gap-3" data-testid="gentle-mode-banner">
            <ShieldCheck className="h-4 w-4 mt-1 text-[#A8AD94] shrink-0" />
            <div>
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">
                {lang === "it" ? "Modalità Routine Delicata attiva" : "Gentle Routine Mode active"}
              </div>
              <p className="text-sm text-[#383633] leading-relaxed">
                {lifeStageNote[lang] || lifeStageNote.en}
              </p>
            </div>
          </div>
        )}

        {!isPaid && isUnder18 && (
          <div className="mt-10 rounded-3xl bg-[#F8F6F2] border kt-line p-8 sm:p-10 kt-shadow text-center" data-testid="under-18-block">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2 flex items-center justify-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> {lang === "it" ? "Sotto i 18 anni" : "Under 18"}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-tight text-[#383633] mb-3">
              {lang === "it" ? "La tua routine delicata è pronta." : "Your gentle routine is ready."}
            </h2>
            <p className="text-sm text-[#6F6B66] max-w-md mx-auto leading-relaxed">
              {lang === "it"
                ? `Il piano a pagamento di €${fmtPrice(priceEur)} è disponibile a partire dai 18 anni. Nel frattempo, continua a esplorare la tua routine delicata e i suggerimenti cosmetici sopra.`
                : `The €${fmtPrice(priceEur)} paid plan is available from age 18. In the meantime, keep exploring your gentle routine and the cosmetic guidance above.`}
            </p>
          </div>
        )}

        {!isPaid && !isUnder18 && (
          <div className="mt-5 sm:mt-8 rounded-3xl bg-white border kt-line p-5 sm:p-8 kt-shadow-md" data-testid="unlock-card">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2 flex items-center gap-2">
              <Sparkles className="h-3 w-3 text-[#D8C4A5]" /> KT Milano · Personalized Plan
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-[1.1] tracking-tight mb-2 text-[#383633]" data-testid="unlock-headline">
              {tr.unlock_headline}
            </h2>
            <p className="text-[#6F6B66] text-sm sm:text-base mb-4 sm:mb-5" data-testid="unlock-sub">{tr.unlock_sub}</p>

            {/* HAVE A PROMO CODE? — same existing logic, now placed above the checklist so the
                promo field + unlock CTA are visible together as high as possible on the page. */}
            <div className="mb-5 rounded-2xl border kt-line bg-[#F8F6F2] p-3 sm:p-4" data-testid="promo-block">
              <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">
                    {lang === "it" ? "Hai un codice promozionale?" : "Have a promo code?"}
                  </div>
                  <div className="text-[11px] text-[#6F6B66] break-words">
                    {appliedPromo
                      ? appliedPromo.will_fully_unlock
                        ? (lang === "it" ? `Codice "${appliedPromo.code}" applicato — accesso gratuito.` : `Code "${appliedPromo.code}" applied — free unlock.`)
                        : (lang === "it"
                            ? `Codice "${appliedPromo.code}" applicato — nuovo prezzo €${appliedPromo.adjusted_price_eur.toFixed(2).replace('.', ',')} (sconto ${appliedPromo.discount_pct}%)`
                            : `Code "${appliedPromo.code}" applied — new price €${appliedPromo.adjusted_price_eur.toFixed(2)} (${appliedPromo.discount_pct}% off)`)
                      : (lang === "it"
                          ? "Inserisci un codice promo o partner per uno sconto (fino al 100 %)."
                          : "Enter a promo or partner code for a discount (up to 100% off).")}
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <input
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder={lang === "it" ? "es. KT-COMP-XXXXXXXX" : "e.g. KT-COMP-XXXXXXXX"}
                    className="rounded-full border kt-line bg-white px-4 h-11 text-sm font-mono uppercase tracking-widest w-full sm:w-52 min-w-0"
                    data-testid="promo-input"
                  />
                  <Button
                    onClick={previewPromo}
                    disabled={promoBusy || !promoInput.trim()}
                    data-testid="promo-apply-btn"
                    className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase px-5 h-11 shrink-0"
                  >
                    {lang === "it" ? "Applica" : "Apply"}
                  </Button>
                </div>
              </div>
            </div>

            <Button
              onClick={unlock}
              disabled={checkoutLoading}
              type="button"
              data-testid="unlock-plan-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white px-6 sm:px-8 py-4 sm:py-6 text-xs kt-tracking uppercase h-auto whitespace-normal leading-tight text-center break-words"
            >
              {checkoutLoading
                ? "…"
                : appliedPromo && !appliedPromo.will_fully_unlock
                  ? (tr.unlock_cta || "").replace("{price}", fmtPrice(appliedPromo.adjusted_price_eur))
                  : appliedPromo && appliedPromo.will_fully_unlock
                    ? (lang === "it" ? "SBLOCCA GRATIS CON IL CODICE PROMO" : "UNLOCK FREE WITH PROMO CODE")
                    : (tr.unlock_cta || "").replace("{price}", fmtPrice(priceEur))
              } <span aria-hidden>→</span>
            </Button>
            {checkoutUrl && (
              <a
                href={checkoutUrl}
                data-testid="checkout-fallback-link"
                className="mt-3 inline-flex items-center gap-2 text-xs kt-tracking uppercase text-[#383633] hover:text-[#4A4238] underline underline-offset-4 decoration-[#D8C4A5]"
              >
                {lang === "it" ? "Se non si apre, clicca qui per continuare" : "If it doesn't open, click here to continue"} <span aria-hidden>→</span>
              </a>
            )}
            <div className="text-[12px] text-[#6F6B66] mt-4" data-testid="unlock-reassurance">
              {tr.unlock_reassurance}
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={() => checkPayment(false)}
                disabled={checkingPayment}
                data-testid="already-paid-btn"
                className="text-[11px] text-[#6F6B66] hover:text-[#383633] underline underline-offset-2 disabled:opacity-50"
              >
                {checkingPayment
                  ? (lang === "it" ? "Verifica in corso…" : "Checking payment…")
                  : (lang === "it"
                      ? "Hai già pagato? Verifica ora →"
                      : "Already paid? Check now →")}
              </button>
            </div>

            {/* Detailed "what's included" — kept below the CTA so users see the price
                and unlock action first, then scroll to read what they get. */}
            <div className="mt-6 pt-5 border-t kt-line">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
                {lang === "it" ? "Cosa è incluso" : "What's included"}
              </div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="unlock-checklist">
                {tr.unlock_checklist.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#383633] leading-relaxed">
                    <Check className="h-4 w-4 mt-0.5 text-[#A8AD94] shrink-0" />
                    <span>{(item || "").replace("{price}", fmtPrice(priceEur))}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        )}

        {isPaid && (
          <div className="mt-10 rounded-3xl bg-[#F8F6F2] border kt-line p-8 sm:p-10 kt-shadow" data-testid="paid-open-card">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">KT Milano · Personalized Plan</div>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight tracking-tight text-[#383633] mb-4">
              {tr.paid_open_title}
            </h2>
            <Link
              to={`/plan/${resultId}`}
              data-testid="view-plan-btn"
              className="inline-flex items-center gap-3 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white px-8 py-4 text-xs kt-tracking uppercase transition"
            >
              {tr.paid_open_cta} <span aria-hidden>→</span>
            </Link>
          </div>
        )}

        {/* Bottom ordering per spec:
           1. (paid recos/routine/plan live on /plan) — small KT Milano banner
           2. Optional partner banner (only when eligible + complete)
           3. Private-result link
           4. Privacy / cosmetic disclaimer */}
        <div className="mt-12">
          <KTBanner size="small" lang={lang} />
        </div>

        <div className="mt-10 border kt-line rounded-2xl p-5 bg-white flex items-center justify-between kt-shadow" data-testid="share-link">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.share_link}</div>
            <div className="text-sm text-[#383633] break-all mt-1">{window.location.href}</div>
          </div>
          <Button onClick={copyLink} variant="ghost" data-testid="copy-link-btn"
            className="rounded-full border kt-line hover:bg-[#F8F6F2] text-[#383633] text-xs kt-tracking uppercase h-auto py-2 shrink-0">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">{tr.copy}</span>
          </Button>
        </div>

        <div className="mt-10 text-[10px] text-[#6F6B66] leading-relaxed border-t kt-line pt-6">
          {tr.footer_disclaimer}
        </div>
      </div>
    </div>
  );
}
